// Sanitet & personell: tap-sporing (PERSTAT — KIA/WIA/MIA), triage-kategorier
// (NATO T1–T4/DEAD) og MIST-rapport (Mechanism/Injuries/Signs/Treatment). Deles i
// sanntid over hubben (som RFI). Gir sjefen levende stridsevne-/PERSTAT-bilde.
//
// Rent + testbart; DoS-caps på alle fritekstfelt.
import { z } from 'zod';
// ── Personellstatus (PERSTAT) ───────────────────────────────────────────────
export const PersonnelStatus = z.enum(['ok', 'wia', 'kia', 'mia', 'captured']);
export const PERSONNEL_STATUS_LABEL = {
    ok: 'Stridsdyktig',
    wia: 'Såret (WIA)',
    kia: 'Falt (KIA)',
    mia: 'Savnet (MIA)',
    captured: 'Tatt til fange',
};
export const PERSONNEL_STATUS_COLOR = {
    ok: '#4bd15b', wia: '#ffd24a', kia: '#ff3b3b', mia: '#c86bff', captured: '#ff8a3d',
};
// ── Triage (NATO) ───────────────────────────────────────────────────────────
export const TriageCategory = z.enum(['t1', 't2', 't3', 't4', 'dead']);
export const TRIAGE_LABEL = {
    t1: 'T1 — Umiddelbar (rød)',
    t2: 'T2 — Haster (gul)',
    t3: 'T3 — Forsinket (grønn)',
    t4: 'T4 — Forventet (blå)',
    dead: 'Død (svart)',
};
export const TRIAGE_COLOR = {
    t1: '#ff3b3b', t2: '#ffd24a', t3: '#4bd15b', t4: '#5b9bd5', dead: '#1a1a1a',
};
const TRIAGE_RANK = { t1: 0, t2: 1, t3: 2, t4: 3, dead: 4 };
// ── MIST-rapport (skades-brief) ─────────────────────────────────────────────
export const MistSchema = z.object({
    mechanism: z.string().max(500).default(''), // M: skademekanisme
    injuries: z.string().max(500).default(''), // I: skader/sted
    signs: z.string().max(500).default(''), // S: vitale tegn (puls/BT/GCS)
    treatment: z.string().max(500).default(''), // T: gitt behandling
});
// ── Tap / pasient ───────────────────────────────────────────────────────────
export const CasualtySchema = z.object({
    id: z.string().max(64),
    /** Kallesignal / navn (eller pseudonym av OPSEC-hensyn). */
    callsign: z.string().max(64),
    status: PersonnelStatus.default('wia'),
    /** Triage settes for WIA; udefinert for OK/MIA/KIA om ikke relevant. */
    triage: TriageCategory.optional(),
    mist: MistSchema.optional(),
    /** Enhet/lag (kort org-etikett). */
    unit: z.string().max(64).default(''),
    lat: z.number().min(-90).max(90).optional(),
    lon: z.number().min(-180).max(180).optional(),
    /** Krever MEDEVAC? (kobler mot 9-line MEDEVAC-flyten.) */
    medevac: z.boolean().default(false),
    reportedBy: z.string().max(128).default(''),
    tsMs: z.number(),
    updatedMs: z.number().default(0),
});
export const MAX_CASUALTIES = 2000;
/** Parser én (upålitelig) tap-melding trygt — null om ugyldig (collab-inntak). */
export function parseCasualtyOne(raw) {
    const r = CasualtySchema.safeParse(raw);
    return r.success ? r.data : null;
}
/** Monotonisk fletting av to kopier (sanntid): nyeste updatedMs vinner.
 * MEDEVAC-flagget bevares (livssikkerhet — mistes aldri ved samtidig redigering). */
export function mergeCasualty(a, b) {
    const winner = b.updatedMs >= a.updatedMs ? b : a;
    return winner.medevac ? winner : { ...winner, medevac: a.medevac || b.medevac };
}
/** Sorter: alvorligst triage først (T1→dead), deretter nyeste. WIA prioriteres. */
export function sortCasualties(cs) {
    return [...cs].sort((a, b) => {
        const ta = a.triage ? TRIAGE_RANK[a.triage] : 99;
        const tb = b.triage ? TRIAGE_RANK[b.triage] : 99;
        return ta - tb || b.tsMs - a.tsMs;
    });
}
/**
 * Hastegrad for et tap — ÉN kilde (§3) for både hendelsesloggens fargestripe og
 * eventuelle framtidige varselsregler.
 *
 * UX-funn F8: «en CONTACT/TIC eller MEDEVAC ser i loggen ut som en hvilken som
 * helst chatlinje». For tap er triagekategorien allerede det militære svaret på
 * «hvor fort», så den skal brukes — ikke en ny skala vi finner på.
 *
 * ⚠ MERK RESONNEMENTET PÅ `dead`. Triage-svart betyr at ingen minutter er
 * avgjørende for pasienten, og presedens handler nettopp om hastverk. Likevel er
 * ikke en falden rutine: meldingen endrer ORBAT og skal fram til sjefen straks.
 * Derfor PRIORITY og ikke ROUTINE — men ikke IMMEDIATE, for det ville satt en
 * bekreftet falden foran en T1 som ennå kan redde livet. Det er dét
 * prioritetsrekkefølge er til for.
 *
 * ⚠ MEDEVAC-FLAGGET LØFTER ALLTID TIL IMMEDIATE, uansett triage. Et tap som er
 * meldt til evakuering venter på en helikopterbeslutning som noen må ta nå.
 */
export function casualtyPrecedence(c) {
    if (c.medevac)
        return 'immediate';
    if (c.triage) {
        if (c.triage === 't1')
            return 'immediate';
        if (c.triage === 't2')
            return 'priority';
        // T3 (forsinket), T4 (forventet) og svart: alvorlig, men ikke minutt-kritisk.
        return c.triage === 'dead' ? 'priority' : 'routine';
    }
    // Ingen triage satt. Da må statusen bære det, og en SÅRET UTEN TRIAGE er verst
    // tenkelige ukjenthet — ingen har vurdert ham ennå.
    if (c.status === 'wia')
        return 'immediate';
    if (c.status === 'kia' || c.status === 'mia' || c.status === 'captured')
        return 'priority';
    return 'routine'; // 'ok' — en stridsdyktig-melding er ren statusføring.
}
/** PERSTAT-sammendrag (stridsevne) fra en tap-liste. */
export function perstat(cs) {
    const p = { total: cs.length, ok: 0, wia: 0, kia: 0, mia: 0, captured: 0, medevac: 0 };
    for (const c of cs) {
        p[c.status]++;
        if (c.medevac)
            p.medevac++;
    }
    return p;
}
/** Formater MIST til lesbar brief (til MEDEVAC-melding / logg). */
export function buildMistText(m) {
    return [
        `M (mekanisme): ${m.mechanism || '—'}`,
        `I (skader): ${m.injuries || '—'}`,
        `S (vitale tegn): ${m.signs || '—'}`,
        `T (behandling): ${m.treatment || '—'}`,
    ].join('\n');
}
//# sourceMappingURL=casualty.js.map